<template>
  <div v-if="bids.length" class="your-bid">
    <h3 class="your-bid__title">{{ bids.length === 1 ? "Your Bid" : `Your Bids (${bids.length})` }}</h3>
    <div v-for="bid in bids" :key="bid._id" class="your-bid__bid">
      <div class="your-bid__row">
        <span>Amount</span>
        <strong>{{ money(bid.amount, currency, 2) }}</strong>
      </div>
      <div class="your-bid__row">
        <span>{{ rateBased ? "Lowest rate" : "Most per share" }}</span>
        <strong>{{ levelOf(bid) }}</strong>
      </div>
      <div class="your-bid__row">
        <span>Status</span>
        <strong>Held in wallet</strong>
      </div>
      <p v-if="releaseNote(bid)" class="your-bid__note">{{ releaseNote(bid) }}</p>

      <div v-if="confirming === bid._id" class="your-bid__confirm" role="alert">
        <span>Withdraw this bid? The {{ money(bid.amount, currency, 2) }} held for it goes back to your wallet.</span>
        <div class="your-bid__actions">
          <button type="button" class="your-bid__btn your-bid__btn--danger" :disabled="busy" @click="$emit('withdraw', bid)" v-can.disable="'invest:trade'">
            {{ busy ? "Withdrawing…" : "Withdraw" }}
          </button>
          <button type="button" class="your-bid__btn" :disabled="busy" @click="confirming = null">Keep it</button>
        </div>
      </div>
      <!-- Changing or withdrawing a bid moves treasury money, so both need invest:trade. -->
      <div v-else class="your-bid__actions">
        <button type="button" class="your-bid__btn" @click="$emit('change', bid)" v-can.disable="'invest:trade'">Change Bid</button>
        <button type="button" class="your-bid__btn your-bid__btn--quiet" @click="confirming = bid._id" v-can.disable="'invest:trade'">Withdraw</button>
      </div>
    </div>
  </div>
</template>

<script>
import { money } from "@/utils/exchange.js";

/**
 * This investor's live bids on an open book, with what happens to the money and the two things
 * they can do before it closes. The page does the change (the bid form, prefilled) and the
 * withdrawal; this asks first, since a withdrawal releases the hold.
 */
export default {
  name: "YourBidCard",
  props: {
    bids: { type: Array, default: () => [] },
    currency: { type: String, default: "NGN" },
    rateBased: { type: Boolean, default: false },
    // The bid being withdrawn right now, so its buttons wait.
    busy: { type: Boolean, default: false },
  },
  emits: ["change", "withdraw"],
  data() {
    return { confirming: null };
  },
  watch: {
    // A withdrawn bid leaves the list; stop asking about it.
    bids(list) {
      if (!list.some((b) => b._id === this.confirming)) this.confirming = null;
    },
  },
  methods: {
    money,
    levelOf(bid) {
      if (bid.is_strike_bid) return this.rateBased ? "Any rate set" : "Any price set";
      if (this.rateBased) return `${Number(bid.bid_rate)}%`;
      return money(bid.bid_price, this.currency, 2);
    },
    releaseNote(bid) {
      if (bid.is_strike_bid) return "You take whatever price is set, so this bid gets shares unless the offer is scaled back.";
      if (this.rateBased) return `If the rate is set below ${Number(bid.bid_rate)}%, nothing is taken and the money is released.`;
      return `If the price is set above ${money(bid.bid_price, this.currency, 2)}, nothing is taken and the money is released.`;
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;
$border-color: #e2e8f0;
$btn-primary: #001240;
$error: #ef4444;

.your-bid {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 20px;

  &__title { font-size: 15px; font-weight: 600; color: $text-primary; margin: 0 0 10px; }

  &__bid + &__bid { border-top: 1px solid $border-color; margin-top: 14px; padding-top: 14px; }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 2;
    span { color: $text-secondary; }
    strong { color: $text-primary; font-weight: 600; }
  }

  &__note { font-size: 12px; color: $text-muted; margin: 6px 0 0; line-height: 1.5; }

  &__confirm {
    margin-top: 12px;
    font-size: 13px;
    color: $text-primary;
    line-height: 1.5;
  }

  &__actions { display: flex; gap: 8px; margin-top: 12px; }

  &__btn {
    flex: 1;
    min-height: 44px;
    border-radius: 8px;
    border: 1px solid $btn-primary;
    background: #ffffff;
    color: $btn-primary;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:disabled { opacity: 0.6; cursor: default; }
    &--quiet { border-color: $border-color; color: $text-secondary; }
    &--danger { background: $error; border-color: $error; color: #ffffff; }
  }
}
</style>
